import React, { ReactElement } from 'react';
import {
  GridCellProps,
  GridColumn as Column,
  GridColumnMenuProps,
  GridColumnResizeEvent,
  GridNoRecords,
  GridPageChangeEvent,
  GridRowProps,
  GridSelectionChangeEvent,
  GridSortChangeEvent
} from '@progress/kendo-react-grid';
import { Keys } from "@progress/kendo-react-common";
import {
  Container,
  getAvailableTaskIds,
  getBinaryReversed,
  Position,
  PuzzleButton,
  PuzzleCheckbox,
  PuzzleDropDownButton,
  PuzzleFakeCheckbox,
  PuzzleInput,
  PuzzleToolbar,
  withDynamicSize
} from "puzzle";
import { DropDownButtonItemClickEvent } from "@progress/kendo-react-buttons";
import classNames from "classnames";
import {
  DOWNLOAD_PARAMS,
  EDITOR_PARAMS,
  FILES_STATE,
  LOADED_TARGET,
  ONLINE_EDITOR,
  PROPS,
  SortableColumn,
  TARGET,
  TARGET_VIEW_RESPONSE,
  UPLOAD_REQUEST_PARAMS,
  URLS_RESPONSE
} from "../Types";
import { REPORT_DATA } from "../../../SubmissionsList/ActionToolbar/UploadDialog/Types";
import * as _ from "lodash";
import PoliciesManager from "../../../util/Policies";
import { CheckboxChangeEvent } from "@progress/kendo-react-inputs";
import {
  SUBMISSION_STATUSES_MAP_SERVER,
  SUPPORTED_EDITORS,
  defaultCellRender,
  getOnlineEditorIdentifier,
  getTableTdAccessibilityProps,
  popupAlignment,
  quickFilterChangeDelay,
  removeUnnecessaryAttributes,
  setArrowNavigation,
  setTableCaption,
  taskSorter,
  parseRemoteTagFieldData,
  checkAndConvertToStaticMarkup,
  TSC_EDITORS
} from "../../../util/Util";
import { FILE } from "../../../SubmissionsList/ActionToolbar/UploadDialog/ReportDialog/Types";
import { NOTIFICATION } from "../../../Viewport/ToastNotifications/ToastNotifications";
import { COLUMN, FILES_GRID_HEADER_CELL_PROPS, GROUPED_TASK } from "../../../MainGrid/Types";
import WordCountLink from "../../../util/WordCountLink/WordCountLink";
import { Popup } from "@progress/kendo-react-popup";
import { PromoteDemoteAbstract } from "./PromoteDemoteAbstract";
import KendoGridAbstract from "../../../util/KendoGridAbstract/KendoGridAbstract";
import HeaderCell from "puzzle/components/grid/header_cell_renderer/HeaderCell";
import ColumnMenu from "puzzle/components/grid/columnmenu/ColumnMenu";
import ActionExecutorInstance from "../../../SupplierView/TaskList/ActionExecutor/ActionExecutor";
import { ACTION } from "../../../SubmissionsList/ActionToolbar/ActionToolbar";
import { FilterTypes } from "../../../Abstract/GridFilter";
import AnchorPopup from "./Comments/AnchorPopup/AnchorPopup";
import OpenMailLink from "../../../util/OpenMailLink/OpenMailLink";
import { EMAIL_CLIENT_PARAMS } from "../../../util/OpenMailLink/Types";
import { HEADER_CELL_PROPS } from "puzzle/components/grid/header_cell_renderer/Types";
import { MaskedTextBoxChangeEvent } from "@progress/kendo-react-inputs/dist/npm/maskedtextbox/MaskedTextBoxProps";

export class Files extends PromoteDemoteAbstract<PROPS, FILES_STATE> {
  clearFilter: boolean = true;
  closedEditor: string = '';
  DATA: any;
  editorSessionIds: any[] = [];
  editorsList: string[] = [];
  filesGridRef: any;
  onlineEditors: {
    windowRef: Window,
    editor: ONLINE_EDITOR
  }[] = [];
  downloadParams = {
    localizationKit: {
      downloadTmStatistics: true,
      instructions: true,
      sourceFiles: true,
      submissionReferenceFiles: true,
      tmGlossary: true,
      translatableFiles: true,
      transcheckReport: true
    },
    deliverables: {},
    TXLFFiles: {
      translatableFiles: true,
    },
  };
  sortableColumns: {
    field: string,
    sortProperty: string | undefined
  }[] = [];
  filterableColumns = [
    { field: 'batchName', filterProperty: 'batchNameValues' },
    { field: 'phaseName', filterProperty: 'phaseNameValues' },
    { field: 'status', filterProperty: 'phaseStatusValues' },
    { field: 'grade', filterProperty: 'gsStatusCombo' },
    { field: 'targetLanguage', filterProperty: 'targetLanguageValues' },
    { field: 'phaseOwner', filterProperty: 'userNameValues' },
    { field: 'vendorName', filterProperty: 'vendorValues' }
  ];
  alwaysVisibleColumns = ['selected']
  menuButtonRef: any;
  settingsButtonRef: any;
  fieldFilters: Map<string, any> = new Map();
  gridSettingsDropdownRef: any;
  columnButtonConfig: any;
  columnPopupArrowIndex = 0;
  columnResizeTimer: any;
  pingingInterval: any = null;

  public state: FILES_STATE = {
    cancelledTargetsCount: 0,
    data: [],
    dataLoadDone: false,
    disableFilesScroll: false,
    filesGridTasks: [],
    fileNameInputText: '',
    filterMenuOpen: '',
    filters: {},
    filterSubmenuOpen: false,
    gridConfig: [],
    headerSelection: false,
    isClaimAvailable: false,
    isDownloadEnabled: false,
    isUploadEnabled: false,
    lastSelectedIndex: 0,
    massSelectionEnabled: false,
    massSelectionConfiguration: {
      allClaimed: false,
      actionCannotBeReverted: false,
      availableEditors: null,
      demoteCommentRequired: false,
      demotionPhase: null,
      fileHasNoChanges: false,
      fileNotMarkAsCompleted: false,
      hasCompletedFiles: false,
      ipcEnabled: true,
      multiplePhasesSelected: false,
      multipleWorkflowsSelected: false,
      phaseType: '',
      promoteCommentRequired: false,
      promoteDemoteDisclaimer: false
    },
    pageDataPopulated: [],
    pageNumber: 0,
    quickFiltersData: {},
    requestInProgress: false,
    showContextMenu: false,
    skip: 0,
    sort: [{
      dir: 'asc',
      field: 'fileName'
    }],
    showGridColumnSettings: false,
    tagFilterData: [],
    tasks: [],
    total: 0,
    commentTask: undefined
  };

  static defaultProps: PROPS = {
    domPageSize: 50,
    overrideMethods: {},
    pageSize: 200,
    rowHeight: 40,
    selectedFieldName: 'selected',
    submissionData: {},
    urlConfigs: {
      filesTabUrl: '/targetsView',
      massActionUrl: '/submissions/massActionConfiguration',
      summaryInfoUrl: '/targetsView/summaryInfo',
      searchBarInfoUrl: '/submissions/%getParam%/searchBarInfo',
      paramsConfig: {
        folder: 'FILE_STATUS_SEARCH',
        mainParam: 'submissionId'
      }
    },
    userType: '',
    detached: false,
    isSupplierView: false,
    setDynamicSize: () => {
    },
    innerRef: null
  };

  /**
   * @inheritDoc
   */
  moduleDidMount = () => {
    const { projectId } = this.props.submissionData;
    this.setDynamicSize();
    window.addEventListener('resize', () => {
      this.dispatch('handleDrawerResize', { buffer: 300 });
    });
    this.updateClaimAvailability();
    this.dispatch('getFilesDataAndFilters', Position.end, { clearFilter: false, getFilterConfig: true, projectId })
    setTableCaption(this.filesGridRef.element, this.translate('SRL-filesTableCaption'));
    removeUnnecessaryAttributes(this.filesGridRef.element);
    window.addEventListener('click', this.closeGridSettingsDropdown);
  };

  /**
   * Get new submission files only when selection changes
   */
  componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any): void {
    const {
      availableTasks,
      tasks
    } = this.props.submissionData;
    const { mainParam } = this.props.urlConfigs.paramsConfig;
    const entityId = this.props.submissionData[mainParam];

    const selectionChanged = prevProps.submissionData[mainParam] !== entityId;
    if (selectionChanged) {
      this.resetGridData(true, true, true, false);
    }

    let availableTasksChanged = prevProps.submissionData.availableTasks !== availableTasks;
    let tasksChanged = !_.isEqual(prevProps.submissionData.tasks, tasks);
    if (selectionChanged || availableTasksChanged || tasksChanged) {
      this.updateClaimAvailability();
    }
    if (!_.isEqual(prevState.gridConfig, this.state.gridConfig)) {
      this.setAccessibilityForGrid();
    }
    setTableCaption(this.filesGridRef?.element, this.translate('SRL-filesTableCaption'));
    removeUnnecessaryAttributes(this.filesGridRef?.element);
  };

  componentWillUnmount(): void {
    super.componentWillUnmount();
    window.removeEventListener('click', this.closeGridSettingsDropdown);
    if (this.state.gridConfig.length) {
      const params = this.getGridConfigJsonData();
      this.dispatch('folderViewUpdateFiles', Position.start, { params });
    }

    if (this.pingingInterval) {
      clearInterval(this.pingingInterval);
    }
  }

  /**
   * Assign dialog action
   * @param action
   */
  assignFiles = ({ all = false }) => {
    const {
      sourceLanguage,
      targetLanguage,
      supplierId,
      projectId,
      submissionId,
      taskId
    } = this.props.submissionData;

    let params = this.createRequestParams(all, {});
    let filesData: { filesCount: number } = {
      filesCount: _.get(params, 'targetIds')?.length
    };

    if (!filesData.filesCount) {
      filesData = {
        filesCount: this.state.total
      };
    }

    this.dispatch('assignSupplierDialog', '', {
      dialogData: {
        assignRequestParams: { ...params, submissionId },
        summaryInfoParams: {
          ...params,
          submissionId,
          taskId
        },
        projectId,
        sourceLanguage,
        submissionId,
        supplierId,
        targetLanguage,
        taskId,
        ...filesData
      }
    });
  };

  /**
   * AssignedTo column render
   * @param cellProps
   */
  assignedTo = (cellProps: GridCellProps) => {
    const { submissionData } = this.props;
    const { dataItem, field } = cellProps;
    const { assignedTo } = dataItem;

    let value;
    if (field) {
      value = cellProps.dataItem[field]?.name;
    }
    let render: JSX.Element | null = this.loadingCell(cellProps);

    const emailClientParams: EMAIL_CLIENT_PARAMS = {
      recipients: [assignedTo?.email],
      subject: `${submissionData.submissionId} ${submissionData.submissionName}`,
      message: ''
    }
    if (!render) {
      render = (
        <span>
          <OpenMailLink
            emailClientParams={emailClientParams}
            policy={'hasHyperlinkAssigneeNamePolicy'}
          >
            {value}
          </OpenMailLink>
        </span>
      )
    }
    return render;
  };

  /**
   * Grid cell render
   * @param cellProps
   */
  cellRender = (cellProps: GridCellProps): JSX.Element => {
    const { dataItem, field } = cellProps;
    const tdProps = getTableTdAccessibilityProps(cellProps);
    let editedProps = _.cloneDeep(cellProps)
    const sortableClass = 'tp-sortable';
    const cellHasHyperlinkClass = PoliciesManager.hasHyperlinkAssigneeNamePolicy ? 'pd-cell-with-small-h-padding' : '';
    let child;

    switch (field) {
      case 'assignedTo':
        editedProps.className = `tp-assignTo-cell ${sortableClass} ${cellHasHyperlinkClass}`;
        child = this.assignedTo(editedProps);
        break;
      case 'selected':
        editedProps.className = `tp-select-cell ${sortableClass}`;
        child = this.selectCellRenderer(editedProps);
        break;
      case 'fileName':
        editedProps.className = `tp-file-name-cell ${sortableClass}`;
        child = this.fileNameRenderer(editedProps);
        break;
      case 'targetLanguage.value':
      case 'phaseName':
      case'batchName':
        editedProps.className = sortableClass;
        child = defaultCellRender(this.translate, this.loadingCell, editedProps);
        break;
      case 'phaseOwner.name':
        editedProps.className = cellHasHyperlinkClass;
        child = this.phaseOwnerCellRender(dataItem, editedProps);
        break;
      case 'dueDate':
      case 'phaseDueDate':
        let dueDate = dataItem.dueDate || dataItem.phaseDueDate;
        dueDate = dueDate ? new Date(dueDate) : '';
        editedProps.className = sortableClass;
        child = this.dueDateRenderer(dueDate);
        break;
      case 'status':
        editedProps.className = sortableClass;
        child = this.statusRenderer(editedProps);
        break;
      case 'vendorName':
        editedProps.className += ` tp-vendor-name-cell ${sortableClass}`;
        child = this.vendorRenderer(editedProps);
        break;
      case 'wordCount':
      case 'words':
        editedProps.className = `pd-word-count-cell pd-cell-with-small-h-padding ${sortableClass}`;
        child = this.wordsCellRender(editedProps);
        break;
      case 'grade':
        editedProps.className = sortableClass;
        child = this.gradeAndScoringRender(editedProps);
        break;
      case 'comments':
        editedProps.className = 'tp-comments-cell';
        child = this.commentsCellRender(editedProps);
        break;
      default:
        child = <></>
        break;
    }
    return <td className={editedProps.className} {...tdProps}>{child}</td>
  };

  /**
   * Cell renderer for comments column
   * @param cellProps
   */
  commentsCellRender = (cellProps: GridCellProps) => {
    const { dataItem } = cellProps
    const { submissionData, urlConfigs } = this.props;
    const { commentTask } = this.state;
    const { availableTasks, commentsCount, targetId } = dataItem;

    let commentsAvailable = false;
    if (availableTasks) {
      const binaryTasksAndProcessing = getBinaryReversed(availableTasks);
      const availableTaskIds = getAvailableTaskIds(binaryTasksAndProcessing);
      commentsAvailable = commentTask ? availableTaskIds.includes(commentTask.taskId) : false;
    }

    const popupProps = {
      commentsCount,
      commentsAvailable,
      folder: urlConfigs.paramsConfig.folder,
      submissionId: submissionData.submissionId,
      targetId,
      moduleId: `AnchorPopup-${dataItem.targetId}`
    }

    return (
      <>
        <AnchorPopup {...popupProps} />
      </>
    )
  }

  /**
   * Render function for user table column (client side)
   * @param dataItem
   * @param editedProps
   */
  phaseOwnerCellRender = (dataItem: any, editedProps: GridCellProps) => {
    let child = defaultCellRender(this.translate, this.loadingCell, editedProps);
    if (PoliciesManager.hasHyperlinkAssigneeNamePolicy) {
      const { submissionData } = this.props;
      const { phaseOwner } = dataItem;

      const emailClientParams: EMAIL_CLIENT_PARAMS = {
        recipients: [phaseOwner?.email],
        subject: `${submissionData.submissionId} ${submissionData.submissionName}`,
        message: ''
      }

      child = <OpenMailLink
        emailClientParams={emailClientParams}
        policy={'hasHyperlinkAssigneeNamePolicy'}
      >
        {child}
      </OpenMailLink>
    }

    return child;
  }

  /**
   * Check if download action is enabled for selected files
   * @param selectedData
   */
  checkIfDownloadIsEnabled = (selectedData: any): boolean => {
    return selectedData.every((item: any) => item.phaseName === selectedData[0].phaseName && _.includes(['IN_PROGRESS', 'IN_PROCESS', 'READY'], item.status.name));
  };

  /**
   * method for reseting current filter per column
   * @param columnName
   */
  clearCurrentFilter = (columnName: string): void => {
    const { filters } = this.state;
    this.setState({ filters: { ...filters, [columnName]: [] } }, () => {
      this.dispatch('filterFilesGrid', Position.end, { filesGridFilter: this.state.filters });
    })
  };

  /**
   * After pinging is processed as true clears data for closed editor.
   */
  clearLastClosedEditorData = (): void => {
    delete this.editorSessionIds[_.toInteger(this.closedEditor)];
    // @ts-ignore
    delete this.closedEditor;
  };

  /**
   * Close settings dropdown
   */
  closeGridSettingsDropdown = (event: any) => {
    if (!this.gridSettingsDropdownRef?.contains(event.target) && !this.settingsButtonRef?.contains(event.target)) {
      this.setState({ showGridColumnSettings: false });
    }
  };

  /**
   * Sets keydown listener and focuses first element
   */
  columnsPopupOnOpen = () => {
    if (this.gridSettingsDropdownRef) {
      const checkboxList = this.gridSettingsDropdownRef.querySelectorAll('.k-menu-item');
      this.gridSettingsDropdownRef.addEventListener('keydown', (e: any) => setArrowNavigation(this, checkboxList, e));
      checkboxList[this.columnPopupArrowIndex].focus();
    }
  };

  /**
   * Header cell renderer for comments column
   * @param thProps
   */
  commentsHeaderColumnCellRender = (thProps: FILES_GRID_HEADER_CELL_PROPS): JSX.Element => {
    return (
      <span
        className='k-text-ellipsis'
        title={thProps.title}
      >
        <span className={'tp-icon tp-file-comments tp-icon-sm !k-d-flex'} />
     </span>
    )
  }

  /**
   * Used for delaying refresh of page that triggers on filter change
   */
  debouncedPrefilterMainGrid = _.debounce(() => {
    this.dispatch('filterFilesGrid', Position.end, { filesGridFilter: this.state.filters });
  }, quickFilterChangeDelay);

  /**
   * Download translation package for selected submission
   */
  downloadFiles = (event: DropDownButtonItemClickEvent) => {
    const { item } = event;
    const {
      downloadType,
      all: downloadAll = false
    }: {
      downloadType: 'localizationKit' | 'deliverables' | 'TXLFFiles' | 'GLP',
      all: boolean
    } = item;
    let params: DOWNLOAD_PARAMS = {
      downloadType
    };

    params = this.createRequestParams(downloadAll, params) as DOWNLOAD_PARAMS;

    this.startDownload(params, downloadAll);
  };

  /**
   * Dispatches download action
   * @param downloadParams
   * @param downloadAll
   */
  startDownload = (downloadParams: DOWNLOAD_PARAMS, downloadAll: boolean = false) => {
    const { startDownload } = this.props.overrideMethods;
    if (startDownload) {
      startDownload(downloadParams);
    } else {
      this.dispatch('pingUntilDownloadIsReady', '', {
        actionText: this.translate('download').toLowerCase(),
        all: downloadAll,
        toasterModuleId: this.props.detached ? 'ToastNotificationsDrawer' : 'ToastNotificationsViewport',
        params: downloadParams,
        additionalData: {
          afterDownloadOperation: 'refreshApp'
        }
      });
    }
  };

  /**
   * Due date cell render
   */
  dueDateRenderer = (date: Date | string): JSX.Element => {
    return <> {this.translateByNamespace('date.dateWithTime', { date })} </>;
  };

  /**
   * File name cell render
   */
  fileNameRenderer = (cellProps: GridCellProps) => {
    const { dataItem, field } = cellProps;
    let value;
    if (field) {
      value = dataItem[field];
    }
    let renderCondition;
    let fileNameCell = this.loadingCell(cellProps);

    if (!fileNameCell) {
      renderCondition = this.isFileHyperlinked(dataItem);
      fileNameCell = renderCondition ?
        (
          <span role="link" className='tp-link' onClick={() => this.onFileClick(dataItem)} tabIndex={0}>
            {value}
            </span>
        ) : (
          value
        );
    }

    return fileNameCell
  };

  /**
   * Conditions to determinate if fileName should be hyperlinked(Open available)
   * @param dataItem
   */
  isFileHyperlinked = (dataItem: any): boolean => {
    const {
      isFileHyperlinked
    } = this.props.overrideMethods;

    let renderCondition;
    if (isFileHyperlinked) {
      renderCondition = isFileHyperlinked(dataItem);
    } else {
      let externalEditor = _.get(dataItem, 'externalEditors[0]');
      let { nonParsable } = dataItem;
      if (externalEditor) {
        if (externalEditor === "SIDE_BY_SIDE") {
          nonParsable = false; // in case of SxS editor ignore nonParsable property
        } else {
          externalEditor = _.includes(SUPPORTED_EDITORS, externalEditor);
        }
      }
      const isOnHold = _.includes(SUBMISSION_STATUSES_MAP_SERVER.ON_HOLD, _.get(this.props, 'submissionData.submissionStatus'));
      let targetStatus = _.get(dataItem, 'status.name');
      renderCondition = !isOnHold && !['PROCESSED', 'DELIVERED', 'CANCELLED'].includes(targetStatus) && !nonParsable && this.state.isClaimAvailable && externalEditor;
    }

    return renderCondition;
  }

  /**
   * Get's intersection of externalEditors on all selected files.
   * @param selectedData
   */
  filterForAvailableEditors = (selectedData: any): string[] => {
    let intersection = selectedData[0].externalEditors;
    for (let i = 1; i < selectedData.length; i++) {
      intersection = _.intersection(intersection, selectedData[i].externalEditors);
    }
    return intersection;
  };

  /**
   * Returns searchBarInfoUrl value
   */
  getSearchBarInfoUrl = (): string => this.props.urlConfigs.searchBarInfoUrl;

  /**
   *  Files Tab Url getter
   */
  getFilesTabUrl = (): string => this.props.urlConfigs.filesTabUrl;

  /**
   * Parse data for filter
   */
  getFilterValues = (data: any, filterName: string) => {
    const { isSupplierView } = this.props;
    let parsedData: any[] = [];
    let i = 0;
    let length = data.length;
    let current;

    while (i < length) {
      current = data[i];
      if (filterName === 'status' || filterName === 'grade') {
        let status = isSupplierView ? 'taskFileStatuses.' : 'fileStatuses.';
        const keyName = filterName === 'status' ? status : 'gradeStatuses.';
        let localizationKey = keyName + current[1];
        current[0] = this.localizeCell(localizationKey);
      }
      if (!current[1]) {
        current[1] = current[0]
      }
      parsedData.push({
        name: current[0],
        value: current[1]
      });
      i++;
    }

    if (filterName === 'status' || filterName === 'grade') {
      parsedData = _.sortBy(parsedData, ['name'])
    }
    return parsedData;
  };

  /**
   * Generate grid config json data
   */
  getGridConfigJsonData = () => {
    const excludedColumns = ['selected'];
    let orderIndex = 0;
    const gridConfig = this.state.gridConfig.reduce((acc: COLUMN[], curr: COLUMN) => {
      if (!excludedColumns.includes(curr.dataIndex)) {
        const updatedColumn: any = {};
        delete Object.assign(updatedColumn, curr, { orderIndex })['header'];
        acc = [...acc, updatedColumn];
        orderIndex++
      }
      return acc;
    }, []);
    let { field, dir } = this.state.sort[0];
    const sortData = this.sortableColumns.find((col: {
      field: string,
      sortProperty: string | undefined
    }) => col.field === field)
    const sortProperty = sortData ? sortData.sortProperty : ''
    return {
      folder: this.props.urlConfigs.paramsConfig.folder,
      metadata: JSON.stringify({
        ascending: dir === 'asc',
        sortProperty,
        gridConfig: _.sortBy(gridConfig, ['orderIndex'])
      })
    };
  }

  /**
   * Method that sets class for opened or filter active class for header
   */
  getHeaderClass = (columnAlias: string) => {
    const { filterMenuOpen, filters } = this.state;
    const isFilterable = this.filterableColumns.find(column => column.field === columnAlias);
    const isSortable = this.sortableColumns.find(column => column.field === columnAlias);

    const headerClasses = classNames({
      'pd-files-tab-grid-header': columnAlias === 'selected',
      'tp-sortable': isSortable,
      'tp-filter-applied': isFilterable && filters[isFilterable.filterProperty] && filters[isFilterable.filterProperty].length > 0,
      'tp-filter-opened': isFilterable && isFilterable.filterProperty === filterMenuOpen,
      'tp-grid-header-with-filter': isFilterable
    });
    return headerClasses
  };

  /**
   * Required params for '/submissions/massActionConfiguration' request
   */
  getMassActionRequestParams = () => {
    const {
      submissionData,
      urlConfigs
    } = this.props;
    const { massActionUrl: requestUrl } = urlConfigs;
    const { submissionId } = submissionData;
    let { filters } = this.state;

    return {
      requestUrl,
      requestParams: {
        submissionId,
        ...filters
      }
    }
  };

  /**
   * Get order properties needed for OPEN action
   */
  getFileOrderProps = (): {
    ascending: boolean,
    sortProperty: string | undefined
  } => {
    let {
      dir,
      field
    } = this.state.sort[0];
    const sortData = this.sortableColumns.find((col: {
      field: string,
      sortProperty: string | undefined
    }) => col.field === field);
    const sortProperty = sortData ? sortData.sortProperty : ''

    return {
      ascending: dir === 'asc',
      sortProperty
    }
  }

  /**
   * Method for getting pageNumber
   */
  getPageNumber = () => this.state.pageNumber

  /**
   * Returns current scroll position
   */
  getScrollPosition = () => {
    return this.filesGridRef.vs.lastScrollTop; // changed from prevScrollPos due to kendo changes
  };

  /**
   * Returns number of selected records
   */
  getSelectedCount = (): number => {
    const { data } = this.state;
    const count: number = data.reduce((total: number, item: TARGET) => {
      if (item.selected) {
        total += 1;
      }
      return total;
    }, 0);

    return count;
  };

  /**
   * Get params for summaryInfo request
   */
  getSummaryInfoParams = () => {
    let summaryInfoParams = this.prepareRequestParams(0);
    summaryInfoParams = _.omit(summaryInfoParams, ['folder', 'index', 'size', 'orderBy'])

    return {
      summaryInfoUrl: this.props.urlConfigs.summaryInfoUrl,
      summaryInfoRequestParams: summaryInfoParams
    }
  }

  /**
   * Returns tso upload success notification
   */
  getTSOUploadNotification = (pingData: REPORT_DATA): NOTIFICATION => {
    let {
      uploadReportsDto,
      ipcEnabled
    } = pingData;
    let numberOfFiles = 0;

    if (uploadReportsDto?.length) {
      numberOfFiles = uploadReportsDto.reduce((count: number, item: FILE) => {
        if (item.fileUploadStatus === 'UPLOADED') {
          count++;
        }
        return count;
      }, 0);
    }

    return {
      text: this.translate(ipcEnabled ? 'ipcNotificationText' : 'notificationText', { file: numberOfFiles }),
      type: 'success',
      icon: true,
      closable: true
    }
  };

  /**
   *  Method for handling search per file name
   *  @param {MaskedTextBoxChangeEvent} event
   */
  handleInputChange = (event: MaskedTextBoxChangeEvent) => {
    const { filters } = this.state;
    const newFilterObject = _.cloneDeep(filters);
    this.setState({ filters: { ...newFilterObject, fileNameInputText: event.target.value } }, () => {
      this.dispatch('filterFilesGrid', {
        buffer: 300,
        pos: Position.end
      }, { filesGridFilter: this.state.filters });
    })
  }
  /**
   * method that handles keydown for span elements inside th
   * @param e
   */
  handleKeydown = (e: any) => {
    if (e.keyCode === Keys.enter) {
      e.preventDefault()
      const evObj = document.createEvent('Events');
      evObj.initEvent('click', true, false);
      this.menuButtonRef = e.target;
      e.target?.dispatchEvent(evObj);
    }
  }

  /**
   * Forwards event to default grid selection change handler
   */
  handleSelectionChange = (selectionHandler: Function | undefined, syntheticEvent: CheckboxChangeEvent) => {
    const {
      headerSelection,
      massSelectionEnabled
    } = this.state;
    const { pageSize } = this.props;
    let selectedTargets = this.getSelectedCount();
    selectedTargets += syntheticEvent.value ? 1 : -1;

    const allowSelection = !(massSelectionEnabled && headerSelection) && (selectedTargets <= pageSize);
    if (allowSelection) {
      if (selectionHandler) {
        selectionHandler({ syntheticEvent });
      }
    } else {
      this.dispatch('maxFileSelectionExceededWarning', '', {
        message: this.translate('maxFileSelectionExceeded', { limit: pageSize })
      })
    }
  };

  /**
   * Header selection handler
   * @param event
   */
  headerSelectionChange = (event: any) => {
    const checked = event.value;
    const { data, massSelectionEnabled } = this.state;
    const { pageSize } = this.props;
    let newData;

    if (massSelectionEnabled) {
      newData = data.map((item: TARGET) => {
        item.selected = checked;
        return item;
      });
    } else {
      newData = data.map((item: TARGET) => {
        if (_.isNumber(item.Index) && (item.Index < pageSize)) {
          item.selected = checked && this.isDataItemSelectable(item);
        }
        return item;
      });
    }

    this.setState({
      data: newData,
      headerSelection: checked
    });
  };

  /**
   * Updates isClaimAvailable prop relative to POLICY_PD_PROCESS_PM_TASKS project policy.
   */
  updateClaimAvailability = (): void => {
    const {
      projectId
    } = this.props.submissionData;
    const isClaimAvailable = PoliciesManager.getProjectPolicy(projectId, 'hasPdProcessPmTasks');

    this.setState({
      isClaimAvailable
    });
  };

  /**
   * Check if dataItem can be selected
   * NonParsable and files inProcess are currently excluded
   */
  isDataItemSelectable = (dataItem: any) => {
    const status = _.get(dataItem, 'status.name');
    return !['CANCELLED'].includes(status); // 'PROCESSED' removed so completed files could be downloaded
  };

  /**
   * method for filtering is column hidden
   * @param column
   */
  isHidden = (column: any) => !column.hidden

  /**
   * Return loading cell if data is not populated
   * @param cellProps
   */
  loadingCell = (cellProps: GridCellProps) => {
    let result = null;
    if (cellProps.dataItem.targetId === undefined) {
      result = <span className="k-icon k-i-loading" />;
    }

    return result;
  };

  /**
   * Pings opened window's for information if window was closed
   */
  m_pingOnlineEditorWindow = (): void => {
    if (!this.pingingInterval) {
      this.pingingInterval = setInterval(this.m_pingOnlineEditorWindow, 1500, this);
    }

    for (const time in this.onlineEditors) {
      let tsrWindow = this.onlineEditors[time].windowRef;
      if (tsrWindow && tsrWindow.closed) {
        //required for detecting closing TSO/TSR/TSC editor so correct editorSessionId or payloadId can be sent
        this.closedEditor = time;
        const editorType: string = this.onlineEditors[time].editor;
        delete this.onlineEditors[time];

        if (!_.keys(this.onlineEditors).length) {
          clearInterval(this.pingingInterval);
          this.pingingInterval = null;
        }

        this.processOnlineEditorClosing(editorType);
      }
    }
  };

  /**
   * returns array of currently available columns for hididng/displaying
   */
  mapGridConfigItems = () => {
    const { gridConfig } = this.state
    return gridConfig.filter((column: any) => !this.alwaysVisibleColumns.includes(column.dataIndex))
  }

  /**
   * Set new column width on resize
   * @param e
   */
  onColumnResize = (e: GridColumnResizeEvent) => {
    clearTimeout(this.columnResizeTimer);
    const { gridConfig } = this.state;
    const { index } = e;
    let newGridConfigObject = _.cloneDeep(gridConfig);
    if (e.newWidth) {
      newGridConfigObject[index].width = e.newWidth;
    }
    this.columnResizeTimer = setTimeout(() => {
      this.setState({ gridConfig: newGridConfigObject });
    }, 500);
  }

  /**
   * method for updating filter selection
   * @param  property  - for what column filter is enabled ,
   * @param value - fake checkbox state
   */
  onChangeFilters = (property: string, value: string[]) => {
    const {
      filters,
      tagFilterData
    } = this.state;
    let filterValue = tagFilterData[property]?.name;
    let newFilterObject;

    if (value.length) {
      newFilterObject = _.cloneDeep(filters);
      newFilterObject[filterValue] = value;
    } else {
      newFilterObject = _.omit(filters, filterValue);
    }

    this.setState({ filters: { ...newFilterObject } }, this.debouncedPrefilterMainGrid);
  };

  /**
   * Sends request for generating tso-editor url.
   * @param data
   */
  onFileClick = (data: any) => {
    const {
      getEditors
    } = this.props.overrideMethods;
    const editor = getEditors ? getEditors() : _.get(data, 'externalEditors');
    const phaseName = _.get(data, 'phaseName');
    if (editor) {
      this.dispatch('getOnlineEditorAddress', '', {
        actionText: this.translate('open').toLowerCase(),
        all: false,
        onlineEditorParams: {
          editor: editor[0],
          entityType: "TARGET",
          shouldAutoCompletePhase: true,
          entityIds: [_.get(data, 'targetId')],
          ...(phaseName && { phaseName })
        },
        openFromFileClick: true,
        urlsEndpoint: this.getOnlineEditorUrl()
      });
    }
  };

  /**
   * method for handling display of columns
   * @param column
   */
  onGridColumnToggle = (column: COLUMN) => {
    const { dataIndex, hidden } = column;
    const newGridConfig = _.cloneDeep(this.state.gridConfig);
    newGridConfig.forEach((el: any) => {
      if (el.dataIndex === dataIndex) {
        el.hidden = !hidden
      }
    })
    this.setState({ gridConfig: newGridConfig })
  }

  /**
   * Checks if column can be sorted and handles sorting
   * @param sort : GridSortChangeEvent
   */
  onSortChange = (e: GridSortChangeEvent) => {
    const { sort } = e;
    const sortData = this.sortableColumns.find((col: SortableColumn) => col.field === sort[0].field)
    if (sort.length > 0 && sortData) {
      this.setState({ sort },
        () => {
          this.dispatch('sortChange');
        });
    }
  };

  onVendorNameClick = (data: any) => {
  };

  /**
   * Open/Preview button render
   * Method uses same localisation for open and preview action from the action.open group.
   * @param groupName
   */
  openButtonRender = (groupName: string): JSX.Element | null => {
    const {
      getEditors
    } = this.props.overrideMethods;
    let {
      cancelledTargetsCount = 0,
      data,
      headerSelection,
      massSelectionEnabled,
      massSelectionConfiguration,
      total,
      tasks
    } = this.state;

    let availableEditors: string[] = [];
    let selected = true;
    let all = true;
    if (getEditors) {
      availableEditors = getEditors();
      if (groupName === 'open') {
        selected = !!_.find(tasks, { taskName: 'open editor' });
        all = !!_.find(tasks, { taskName: 'open all jobphase editor' });
      } else {
        selected = !!_.find(tasks, { taskName: 'jobphase file preview single' });
        all = !!_.find(tasks, { taskName: 'jobphase file preview all' });
      }
    } else {
      availableEditors = massSelectionConfiguration.availableEditors || [];
    }

    TSC_EDITORS.forEach((item: string) => { // reposition of GLT/GLR like we were doing for TSC
      const tscIndex = availableEditors.indexOf(item);
      if (tscIndex !== -1) {
        availableEditors.splice(tscIndex, 1);
        availableEditors.unshift(item);
      }
    })
    let enableOpen = data.some((item: TARGET) => item.selected);

    let openButton = null;
    if (availableEditors && availableEditors.length) {
      //List of available editors can contain numerous editors but for now only TSO and TSR are allowed
      const filteredEditors = [...availableEditors].reduce((array: any[], item: string) => {
        if (_.includes(SUPPORTED_EDITORS, item)) {
          if (availableEditors?.length === 1) {

            if (selected) {
              let selectedFilesCount = this.getSelectedCount();
              if (massSelectionEnabled && headerSelection) {
                selectedFilesCount = selectedFilesCount - cancelledTargetsCount;
              }
              array.push({
                disabled: !enableOpen,
                groupName,
                item,
                text: this.translate(`actions.open.singleEditor`, { length: selectedFilesCount })
              })
            }

            if (all) {
              array.push({
                all: true,
                disabled: false,
                groupName,
                item,
                text: this.translate(`actions.open.all`, { length: (total - cancelledTargetsCount) })
              })
            }
          } else {

            if (selected) {
              const text = this.translate(`actions.open.editor`, { editor: this.translate(`editorNames.${item}`) });
              array.push({
                disabled: !enableOpen,
                groupName,
                item,
                text
              })
            }

            if (all) {
              const text = this.translate(`actions.open.editorAll`, { editor: this.translate(`editorNames.${item}`) });
              array.push({
                all: true,
                disabled: false,
                groupName,
                item,
                text
              })
            }
          }
        }
        return array
      }, []);

      if (filteredEditors.length) {
        openButton = (
          <PuzzleDropDownButton
            key={`${groupName}_group`}
            icon={'arrow-s'}
            popupSettings={popupAlignment('')}
            items={filteredEditors}
            onItemClick={this.itemClick}
            reverse
            text={this.translate(`action_group_names.${groupName}`)}
            themeColor={'dark'}
          />)
      }
    }

    return openButton
  };

  /**
   * Getter for online editor adress
   */
  getOnlineEditorUrl = (): string => {
    const {
      submissionData,
      overrideMethods
    } = this.props;
    const {
      getOnlineEditorUrl
    } = overrideMethods;
    const { submissionId } = submissionData;

    return getOnlineEditorUrl ? getOnlineEditorUrl() : `/submissions/${submissionId}/editor/urls`;
  };

  /**
   * Used to open editor by clicking on open button action, starts dispatcher action for all selected files
   */
  openInEditor = (
    { item: editor, all }: {
      item: string,
      all: boolean
    },
    groupName: string
  ) => {
    const {
      submissionData,
      urlConfigs
    } = this.props;
    const {
      mainParam
    } = urlConfigs.paramsConfig;
    const entityId: number = submissionData[mainParam];
    const {
      data,
      fileNameInputText,
      filters,
      headerSelection,
      massSelectionEnabled
    } = this.state;

    let params: EDITOR_PARAMS = {
      editor: editor,
      entityType: "TARGET"
    };

    const selectedRecords = data.filter(item => ((all || item.selected) && item.status?.name !== 'CANCELLED')) as LOADED_TARGET[];
    if ((headerSelection || all) && massSelectionEnabled) {
      const {
        ascending,
        sortProperty
      } = this.getFileOrderProps();

      params = {
        activeFilters: {
          ...filters,
          ...(fileNameInputText && { fileNameInputText }),
          [mainParam]: entityId,
          ascending,
          sortProperty
        },
        ...params,
      }

    } else {
      params = {
        ...params,
        entityIds: selectedRecords.map(item => item.targetId)
      }
    }
    let phaseName = _.get(selectedRecords, '[0].phaseName');
    params = {
      ...params,
      ...(phaseName && { phaseName })
    }
    if (groupName === 'open') {
      let urlsEndpoint = this.getOnlineEditorUrl();
      this.dispatch('getOnlineEditorAddress', '', {
        actionText: this.translate('open').toLowerCase(),
        all: all,
        urlsEndpoint,
        onlineEditorParams: {
          ...params
        }
      });
    } else {
      ActionExecutorInstance.previewTask(
        submissionData,
        { ...params },
        this.getOnlineEditorUrl(),
        'post'
      );
    }
  };

  /**
   * Page event handler, set skip
   */
  pageChange = (event: GridPageChangeEvent) => {
    const skip = event.page.skip;
    this.requestIfNeeded(skip);
    this.setState({
      skip: skip
    });
  };

  /**
   * Grid header cell render
   * @param thProps
   */
  puzzleHeaderRender = (thProps: HEADER_CELL_PROPS): JSX.Element => {
    return <HeaderCell
      {...thProps}
    />;
  }

  /**
   * Method that prepares prefiltered data coming form status tab
   * @param data - filter from status tab
   */
  preFilterFromStatusTab = (preFilters: any) => {
    const { filters } = this.state;
    this.setState({ filters: { ...filters, ...preFilters } }, () => {
      this.dispatch('filterFilesGrid', Position.end, { filesGridFilter: this.state.filters });
    })
  }

  /**
   * Opens online editor based on passed url
   * @param onlineEditorObject
   * @param onlineEditorRequestParams
   */
  prepareEditorOpen = (onlineEditorObject: URLS_RESPONSE, { editor: requestParamsEditor }: { editor: ONLINE_EDITOR }): void => {
    // on opening of processed file server returns empty array
    if (onlineEditorObject.length === 1) {
      const onlineEditor = onlineEditorObject[0];
      const {
        captive,
        editorUrl
      } = onlineEditor;
      const time = new Date().getTime();
      // default params configuration is set to TSC
      let editor: ONLINE_EDITOR = requestParamsEditor;
      let editorIdentifierParams: Parameters<typeof getOnlineEditorIdentifier> = [editorUrl, 'payloadId'];
      let windowParams: Parameters<typeof window.open> = [editorUrl];

      if (!editorUrl.includes('payloadId')) { // TSC link contains payloadId instead of editorSessionId
        const captiveModeParams = '"location=no,status=no,toolbar=no,titlebar=no,menubar=no,directories=no,personalbar=no,dialog=yes,chrome=yes,resizable",';
        const urlWithTheme = editorUrl + '&theme=classicPrimavera';
        editor = 'TSO/TSR';
        editorIdentifierParams = [editorUrl];
        windowParams = [urlWithTheme];

        if (onlineEditor && captive) {
          windowParams = [urlWithTheme, '', captiveModeParams];
        }
      }

      this.onlineEditors[time] = {
        windowRef: window.open(...windowParams) as Window,
        editor
      };
      this.editorSessionIds[time] = getOnlineEditorIdentifier(...editorIdentifierParams);

      if (!this.pingingInterval) {
        setTimeout(this.m_pingOnlineEditorWindow, 1000, this);
      }
    }
  };

  /**
   * Prepares request params
   * @param pageNumber
   */
  prepareRequestParams = (pageNumber: number, sort ?: boolean) => {
    const {
      pageSize,
      submissionData,
      urlConfigs
    } = this.props;
    const {
      folder,
      mainParam
    } = urlConfigs.paramsConfig;
    const { submissionId } = submissionData;
    const entityId: number = submissionData[mainParam];
    let { field, dir } = this.state.sort[0];
    const { gridConfig, filters } = this.state;
    const excludedColumns = ['selected'];

    let newGridConfig = gridConfig.reduce((acc: COLUMN[], curr: COLUMN) => {
      if (!excludedColumns.includes(curr.dataIndex)) {
        const updatedColumn: any = {};
        delete Object.assign(updatedColumn, curr)['header'];
        acc = [...acc, updatedColumn]
      }
      return acc;
    }, []);
    const sortData = this.sortableColumns.find((col: {
      field: string,
      sortProperty: string | undefined
    }) => col.field === field)
    const sortProperty = sortData ? sortData.sortProperty : ''
    let params = {
      folder,
      size: pageSize,
      orderBy: mainParam,
      [mainParam]: entityId,
      ...(mainParam === 'taskId' && { submissionId }),
      index: pageNumber ? pageNumber : 0,
      ...filters,
    };
    if (sort) {
      params = {
        ...params,
        metadata: JSON.stringify({
          ascending: dir === 'asc',
          sortProperty,
          gridConfig: _.sortBy(newGridConfig, ['orderIndex'])
        })
      }
    }
    return params
  };

  /**
   * Starts batch pinging through dispatcher
   * @param editorType
   */
  processOnlineEditorClosing = (editorType: string): void => {
    const editorSessionId = this.editorSessionIds[_.toInteger(this.closedEditor)];
    let dispatchActionName = 'processEditorClose';
    if (_.includes(TSC_EDITORS, editorType)) {
      dispatchActionName = 'processTscClose';
    }
    this.dispatch(dispatchActionName, {
      pos: Position.end,
    }, {
      pingParam: {
        editorSessionId
      }
    });
  };

  /**
   * Triggers refreshFilesGrid action's dispatcher flow
   */
  refreshFilesGrid = (): void => this.dispatch('refreshApp', '', {
    refreshFromFilesTab: true,
    preserveSkip: true,
    toasterModuleId: this.props.detached ? 'ToastNotificationsDrawer' : 'ToastNotificationsViewport'
  });

  /**
   * method that renders files grid columns
   * @param column
   * @param index
   */
  renderColumn = (column: any, index: Number) => {
    let dataIndex: string = column.dataIndex;
    const columnAlias = dataIndex.split('.')[0];
    const isFilterable = this.filterableColumns.find(filterableColumn => filterableColumn.field === columnAlias);
    const isSortable = !!(this.sortableColumns.find(sortableColumn => sortableColumn.field === dataIndex));
    let headerCellRenderer;
    switch (dataIndex) {
      case 'selected':
        headerCellRenderer = this.selectedHeaderColumnCellRender;
        break;
      case 'comments':
        headerCellRenderer = this.commentsHeaderColumnCellRender;
        break;
      default:
        headerCellRenderer = this.puzzleHeaderRender;
    }

    let nonResizableColumns = ['selected', 'comments', 'grade'];
    let columnProps: any = {
      field: dataIndex,
      key: dataIndex,
      minResizableWidth: 120,
      headerCell: headerCellRenderer,
      title: this.translate(`columnHeaders.${columnAlias}`),
      cell: this.cellRender,
      columnMenu: (isFilterable || isSortable) && this.renderColumnMenuCheckboxFilter,
      sortable: isSortable,
      resizable: !nonResizableColumns.includes(dataIndex),
      width: column.width,
      headerClassName: this.getHeaderClass(dataIndex.split('.')[0])
    };
    return <Column {...columnProps} />
  }

  /**
   *Method for rendering filter dorpdown menu
   * @param props :GridColumnMenuProps
   * */
  renderColumnMenuCheckboxFilter = (props: GridColumnMenuProps): JSX.Element | null => {
    const {
      filters,
      tagFilterData
    } = this.state;
    const columnName = props.column.field?.split('.')[0] || '';
    let currentData = tagFilterData[columnName]?.values ? this.getFilterValues(tagFilterData[columnName].values, columnName) : [];
    let filterName = tagFilterData[columnName]?.name;
    const filterData = tagFilterData[columnName];
    const isFilterable = this.filterableColumns.find(column => column.field === columnName);
    const isSortable = this.sortableColumns.find(column => column.field === columnName);

    let columnMenuProps: any = {
      data: currentData,
      closeMenu: props.onCloseMenu,
      filterable: false,
      searchType: 'fullText',
      sortable: false,
      values: filters[filterName] || []
    };

    if (filterData) {
      switch (filterData.type) {
        case FilterTypes.number:
          columnMenuProps.type = 'input-numeric';
          break;
        case FilterTypes.text:
          columnMenuProps.type = 'input';
          break;
        case FilterTypes.dropDown :
          columnMenuProps.type = 'list-search';
          if (filterData.name === "gsStatusCombo") {
            columnMenuProps.type = 'list';
          }
          break;
        case FilterTypes.singleCombo:
          columnMenuProps.type = 'list';
          columnMenuProps.singleSelectList = true;
          break;
        case FilterTypes.remoteTagField:
          columnMenuProps = {
            ...columnMenuProps,
            parseData: (data: any) => parseRemoteTagFieldData(data, columnName),
            requestConfig: {
              url: '/remoteCombo',
              method: 'post',
              params: { valuesProvider: filterData.valuesProvider }
            },
            type: 'virtual-dom-list-search'
          }
          break;
        default:
          columnMenuProps.type = 'input';
          break;
      }
    }

    //add filter props
    if (isFilterable) {
      columnMenuProps = {
        ...columnMenuProps,
        filterable: true,
        onFilterChange: this.onChangeFilters
      }
    }

    // add sort props
    if (isSortable) {
      columnMenuProps = {
        ...columnMenuProps,
        sortable: true,
        onSortChange: this.onSortChange,
      }
    }

    return <ColumnMenu
      setActiveHeader={this.setFilterMenuOpen}
      column={{ field: columnName }}
      {...columnMenuProps}
    />
  };

  /**
   * method that renders grid config
   * @param column
   */
  renderGridConfigItem = (column: any) => {
    const { hidden, dataIndex } = column
    const cls = classNames(
      'k-item k-menu-item',
      {
        'k-selected': !hidden
      }
    )
    return (
      <li
        className={cls}
        tabIndex={0}
        key={`grid-config-item-${dataIndex}`}
        onClick={this.onGridColumnToggle.bind(this, column)}
      >
        <span className={'k-link k-menu-link'} role={'presentation'}>
          <PuzzleFakeCheckbox
            checked={!hidden}
            label={this.translate(`columnHeaders.${dataIndex.split('.')[0]}`)}
          />
        </span>
      </li>
    )
  }

  /**
   * Request new page of data
   */
  requestData = (pageNumber: number) => {
    // request data only if not already fetched
    if (this.state.requestInProgress) {
      //perform only one request at a time
      return;
    }
    let { pageDataPopulated } = this.state;
    pageDataPopulated.push(pageNumber);
    let params = this.prepareRequestParams(pageNumber);
    this.setState({ requestInProgress: true, pageNumber: pageNumber, pageDataPopulated }, () => {
      this.dispatch('getFilesGridData', Position.end, { params })
    });
  };

  /**
   * Call request for new page of data if needed
   */
  requestIfNeeded = (skip: number) => {
    const { pageSize } = this.props;
    const { data, pageDataPopulated, pageNumber: lastPageNumber } = this.state;
    for (let i = skip; i < skip + pageSize && i < data.length; i++) {
      // @ts-ignore
      if (data[i].targetId === undefined) {
        let pageNumber = Math.ceil((i + pageSize) / pageSize) - 1;
        if (pageNumber === lastPageNumber) {
          pageNumber--; //load previous page when scrolling up from down
        }

        if (!pageDataPopulated.includes(pageNumber)) {
          this.requestData(pageNumber);
        } else {
          //fix for scrolling up, don't allow to get stuck without data
          pageNumber--;
          if (!pageDataPopulated.includes(pageNumber)) {
            this.requestData(pageNumber);
          }
        }
        break;
      }
    }
  };

  /**
   * Method for reseting filters and sort
   */
  resetFilters = () => {
    this.setState({
      filters: { fileNameInputText: '' }, sort: [{
        field: 'fileName',
        dir: 'asc'
      }]
    }, () => {
      this.dispatch('sortChange', Position.end, { filesGridFilter: {} });
    })
  };

  /**
   * Reset grid data and all paging params
   */
  resetGridData = (clearFilter: boolean, forceRequest: boolean, getFilterConfig: boolean, preserveSkip: boolean) => {
    const newState: any = {
      data: [],
      filesGridTasks: [],
      dataLoadDone: false,
      headerSelection: false,
      massSelectionEnabled: false,
      pageDataPopulated: [],
      pageNumber: 0,
      tasks: [],
      ...(!preserveSkip && { skip: 0 })
    };
    this.setState({ ...newState }, () => {
      if (forceRequest) {
        const { projectId } = this.props.submissionData;
        this.dispatch('getFilesDataAndFilters', Position.end, { clearFilter, getFilterConfig, projectId });
      }
    });
  };

  /**
   * Row renderer, add class for cancelled files
   */
  rowRender = (trElement: ReactElement<HTMLTableRowElement>, rowProps: GridRowProps) => {
    const elementProps = { ...trElement.props };
    const status = rowProps.dataItem.status;
    const isCancelled: boolean = status && status.name === 'CANCELLED';

    if (isCancelled) {
      elementProps.className += ' tp-row-file-cancelled tp-text-italic';
    }

    const trProps = {
      ...elementProps
    };

    return React.cloneElement(trElement, { ...trProps }, elementProps.children);
  }

  /**
   * Moves vertical scroll on grid to desired position
   * @param scrollPosition
   */
  scrollToLastPosition = (scrollPosition: number) => {
    this.clearLastClosedEditorData();
    const virtualContainer = this.filesGridRef.vs.container;
    virtualContainer.scrollTo(0, scrollPosition);
  };

  /**
   * Render for displaying search results
   */
  searchResultsRender = (): JSX.Element => {
    let {
      cancelledTargetsCount,
      headerSelection,
      massSelectionEnabled,
      total = 0
    } = this.state;
    let totalWithoutCancelled = total - cancelledTargetsCount;
    const selectedCount = (massSelectionEnabled && headerSelection) ? totalWithoutCancelled : this.getSelectedCount();
    return (
      <span className={'tp-search-results k-d-flex k-align-items-center'}>
        <span className={`tp-total-files-count tp-font-size-sm ${total ? "" : "tp-no-files"}`}>
          {this.translate('filesCount', { files: total })}
        </span>
        <span className={'tp-separator'}>|</span>
        <span className={`tp-selected-files-count tp-font-size-sm ${selectedCount ? "" : "tp-no-files"}`}>
          {this.translate('selectedFilesCount', { files: selectedCount })}
        </span>
      </span>
    )
  };

  /**
   * Renders select cell
   */
  selectCellRenderer = (cellProps: GridCellProps) => {
    const { selectedFieldName } = this.props;
    const {
      dataItem,
      selectionChange
    } = cellProps;

    let selectCell = this.loadingCell(cellProps);
    if (!selectCell) {
      selectCell = this.isDataItemSelectable(dataItem) ? (
        <PuzzleCheckbox
          aria-label={this.translate("SRL-checkboxLabel")}
          checked={dataItem[selectedFieldName]}
          onChange={this.handleSelectionChange.bind(this, selectionChange)}
        />
      ) : <span aria-hidden={true} className={'tp-icon tp-icon-xs tp-checkbox-cancelled'} />;
    }
    return selectCell;
  };

  /**
   * Custom selected column header cell render
   */
  selectedHeaderColumnCellRender = () => {
    const { headerSelection } = this.state;
    return (
      <PuzzleCheckbox
        checked={headerSelection}
        className={'tp-drawer-files-grid-header-select-cell'}
        onChange={this.headerSelectionChange}
        aria-label={this.translate("SRL-checkboxHeaderLabel")}
      />
    )
  };

  /**
   * Selection change handler
   */
  selectionChange = (event: GridSelectionChangeEvent) => {
    let currentDataItem = event.dataItem;
    let { data } = this.state;
    let { selectedFieldName } = this.props;

    let arrayIndex = data.findIndex((currentValue) => currentValue === currentDataItem);
    let item = data[arrayIndex];

    item.selected = !currentDataItem[selectedFieldName];

    let selectableData = data.filter(item => this.isDataItemSelectable(item));
    const headerSelection = selectableData.every(item => item.selected);

    this.setState({
      data,
      headerSelection
    });
  };

  /**
   * sets focusable headers
   */
  setAccessibilityForGrid = () => {
    const { element } = this.filesGridRef;
    const thList = element.querySelectorAll('th');
    thList.forEach((th: HTMLTableHeaderCellElement) => {
      // const filterMenu = th.querySelector('span.k-i-more-vertical');
      th?.setAttribute('tabindex', '0');
      /*filterMenu?.setAttribute('tabindex', '0');
      filterMenu?.setAttribute('role', 'button');
      filterMenu?.setAttribute('aria-label', this.translate('columnFilter'));
      filterMenu?.addEventListener('keydown', this.handleKeydown.bind(this), { capture: true });
      filterMenu?.addEventListener('click', (e: any) => this.menuButtonRef = e.target);
      TODO do accessibility check looks like unnecessary code
      */
    })
  }


  /**
   * Set cancelled files number
   */
  setCancelledFilesNumber = (summaryInfo: any) => {
    let { cancelledTargetsCount } = summaryInfo;
    this.setState({
      cancelledTargetsCount
    });
  }

  /**
   * Set data
   */
  setData = (data: any) => {
    if (!this.DATA) {
      this.DATA = data.filterData;
      this.setState({
        data: data.filterData
      });
    }
  };

  /**
   * methods that set currently opened filter menu for given column
   * @param columnName - name of column
   */
  setFilterMenuOpen = (columnName?: string) => {
    this.setState({ filterMenuOpen: columnName ? columnName : '' })
  }

  /**
   * Setting grid config data and sortable columns
   * @param  data - response data
   * */
  setGridConfig = (data: TARGET_VIEW_RESPONSE) => {
    const sortInfo = _.get(data, "gridContentInfo.pagedListInfo");
    let sortableColumns: SortableColumn[] = []
    let gridConfig = data.gridConfig.reduce((acc: any, curr: COLUMN) => {
      const updatedColumn: any = {};
      if (curr.sortable) {
        sortableColumns.push({ field: curr.dataIndex.split('.')[0], sortProperty: curr.sortProperty });
      }
      delete Object.assign(updatedColumn, curr, { 'headerKey': curr.header })['header'];
      acc = [...acc, updatedColumn]
      return acc;
    }, []);
    this.sortableColumns = sortableColumns;
    gridConfig = [{
      dataIndex: "selected",
      hidden: false,
      sortable: true,
      systemHidden: false,
      width: 43
    }, ...gridConfig];
    const gridItem = _.find(gridConfig, { sortProperty: sortInfo.sortProperty });
    const sort = gridItem ? [{
      dir: (sortInfo.ascending ? 'asc' : 'desc'),
      field: gridItem.dataIndex
    }] : this.state.sort;

    // Checking if sort is actually different when this is called (to save those details about what sort is active when page is refreshed)
    // Otherwise for example if I set it to be sorted by batch and refresh page it will be sorted by batch but arrows will show it is sorted by something else
    if (!_.isEqual(this.state.sort, sort)) {
      this.setState({ gridConfig, sort });
    } else {
      this.setState({ gridConfig });
    }
  };

  /**
   * Parse data and set it to grid
   */
  setGridData = (data: TARGET_VIEW_RESPONSE) => {
    let { items = [], gridContentInfo } = data;
    const total = gridContentInfo.totalCount;
    if (items.length) {
      const allData: any = this.state.data.length === total ?
        this.state.data : new Array(total).fill(undefined, 0, total).map((e: any, i: number) => ({ Index: i }));

      let { headerSelection, massSelectionEnabled, pageNumber } = this.state;
      const { pageSize } = this.props;
      let indexForAdd = pageSize * pageNumber;

      items.forEach((item: any) => {
        allData[indexForAdd] = {
          Index: indexForAdd,
          selected: headerSelection,
          ...item
        };
        indexForAdd++;
      });

      if (!massSelectionEnabled) {
        massSelectionEnabled = total > pageSize
      }

      let { tasks = [] } = this.state;
      tasks = _.unionBy(tasks, data.tasks, 'taskName');
      let sortedTasks = taskSorter(tasks);

      // @ts-ignore
      this.setState({
        data: allData,
        dataLoadDone: true,
        requestInProgress: false,
        filesGridTasks: sortedTasks,
        total: total,
        tasks,
        commentTask: _.find(tasks, { groupName: 'comment', taskName: 'add comment' }),
        ...(massSelectionEnabled && { massSelectionEnabled })
      })
    } else {
      this.setState({
        dataLoadDone: true,
        requestInProgress: false,
        total
      });
    }
    this.requestIfNeeded(this.state.skip);
  };

  /**
   * Calculates height of the grid with function from HOC
   */
  setDynamicSize = () => this.props.setDynamicSize();

  /**
   * Setting filter data response from server
   * @param  filterData - response data
   * */
  setTagFilterData = (filterData: any) => {
    //TODO refactor this build this more abstract way
    const {
      TARGET_LANGUAGE_VALUES,
      PHASE_NAME_VALUES,
      VENDOR_VALUES,
      PHASE_STATUS_VALUES,
      BATCH_NAME_VALUES,
      USER_NAME_VALUES,
      ASSIGNED_TO_REMOTE_COMBO,
      GRADE_STATUS_COMBO
    } = filterData;
    const tagFilterData = {
      targetLanguage: { ...TARGET_LANGUAGE_VALUES },
      phaseName: { ...PHASE_NAME_VALUES },
      vendorName: { ...VENDOR_VALUES },
      status: { ...PHASE_STATUS_VALUES },
      batchName: { ...BATCH_NAME_VALUES },
      phaseOwner: { ...USER_NAME_VALUES },
      assignedTo: { ...ASSIGNED_TO_REMOTE_COMBO },
      grade: { ...GRADE_STATUS_COMBO }
    };

    if (ASSIGNED_TO_REMOTE_COMBO) {
      this.filterableColumns.push({
        field: 'assignedTo',
        filterProperty: 'assignedToRemoteCombo'
      });
    }

    this.setState({ tagFilterData });
  };

  /**
   * Status column cell render
   */
  statusRenderer = (cellProps: GridCellProps) => {
    const { isSupplierView } = this.props;
    let cellStatusCls = '';
    let field = cellProps.field?.split('.')[0];
    let value;
    let statusCell = this.loadingCell(cellProps);
    if (field) {
      value = cellProps.dataItem[field];
      value = value ? value.name : null;
    }

    if (value) {
      cellStatusCls = classNames(
        'tp-main-grid-status-cell tp-drawer-status-cell',
        value.toLowerCase());

      let status = isSupplierView ? 'taskFileStatuses.' : 'fileStatuses.';
      let localizationKey = status + value;
      statusCell = (
        <>
          <div
            className={`${cellStatusCls}`}
          >
            {this.localizeCell(localizationKey)}
          </div>
        </>
      )
    }

    return statusCell
  };

  /**
   * Grade and scoring cell render
   * @param cellProps
   */
  gradeAndScoringRender = (cellProps: GridCellProps) => {
    let cellStatusCls = '';
    let field = 'gradingAndScoringStatus';
    let value;
    let gradeCell = this.loadingCell(cellProps);
    if (field) {
      value = cellProps.dataItem[field];
    }
    if (value) {
      cellStatusCls = classNames(
        'tp-main-grid-status-cell tp-drawer-grade-cell',
        value.toLowerCase());

      let localizationKey = 'gradeStatuses.' + value;
      gradeCell = (
        <div
          className={`${cellStatusCls}`}
        >
          {this.localizeCell(localizationKey)}
        </div>
      )
    }

    return gradeCell
  };

  /**
   * Gets proper localization value
   * @param value
   */
  localizeCell = (value: string) => {
    const { localizeCell } = this.props.overrideMethods;
    let localizedValue = this.translate(value);
    if (localizeCell) {
      localizedValue = localizeCell.bind(this)(value);
    }

    return localizedValue;
  }

  /**
   * Complete and Return actions handler
   *
   * @param event
   * @param action
   */
  completeReturnFilesHandler = (action: 'promote' | 'demote', event: DropDownButtonItemClickEvent) => {
    const { completeReturnFilesHandler } = this.props.overrideMethods;
    if (completeReturnFilesHandler) {
      completeReturnFilesHandler.bind(this)(action, event);
    } else {
      const { item } = event;
      const {
        data,
        massSelectionEnabled,
        massSelectionConfiguration,
        headerSelection
      } = this.state;
      const { all: selectAll = false } = item;
      let massAction = massSelectionEnabled && (selectAll || headerSelection);
      let {
        hasCompletedFiles,
        notClaimed,
        isMultiPhase,
        isMultiWorkflow,
        linguistWithoutUpload
      } = this.checkForSelectionIrregularity(action, massAction);

      if (massAction || selectAll) {
        isMultiPhase = _.get(massSelectionConfiguration, 'multiplePhasesSelected');
        isMultiWorkflow = _.get(massSelectionConfiguration, 'multipleWorkflowsSelected');
        hasCompletedFiles = _.get(massSelectionConfiguration, 'hasCompletedFiles');
        linguistWithoutUpload = _.get(massSelectionConfiguration, 'phaseType') === 'linguist' && !_.get(massSelectionConfiguration, 'allFilesUploaded');
        notClaimed = !_.get(massSelectionConfiguration, 'allClaimed');
      }

      let dispatchAction: string = '';
      let actionParams = {};
      if (isMultiPhase) {
        dispatchAction = 'samePhaseWarning';
        actionParams = {
          actionNameLocale: this.translate(`promoteDemoteLocale.${action}`).toLowerCase(),
        };
      } else if (notClaimed) {
        dispatchAction = 'claimNotClaimedFilesWarning';
        actionParams = {
          all: selectAll,
          actionText: this.translate(`promoteDemoteLocale.promote`).toLowerCase() //for this case request is to display "complete" both for complete and return cases
        };
      } else if (isMultiWorkflow) {
        dispatchAction = 'sameWorkflowWarning';
        actionParams = {
          actionNameLocale: this.translate(`promoteDemoteLocale.${action}`).toLowerCase(),
          text: this.translate('sameWorkflow')
        };
      } else if (linguistWithoutUpload) {
        dispatchAction = 'linguistWithoutUploadWarning';
      } else if (hasCompletedFiles) {
        this.dispatch('hasCompletedFilesWarning');
      } else {
        massAction && this.setFlagConfiguration(action);
        dispatchAction = 'startPromoteFiles';
        let previousPhase: any = '';
        if (action !== 'promote') {
          const { demotionPhase } = this.state.massSelectionConfiguration;
          previousPhase = massAction || selectAll ? demotionPhase : _.get(_.find(data, { 'selected': true }), 'demotionPhase');
        }
        actionParams = {
          actionName: action,
          selectAll,
          executionModuleName: this.moduleName,
          previousPhase
        }
      }
      this.dispatch(dispatchAction, '', actionParams);
    }
  };

  /**
   * Returns configuration of warnings used by dispatcher to show/hide proper dialogs
   *
   * @param action
   * @param selectAll
   */
  getWarningFlags = (action: 'promote' | 'demote', selectAll: boolean = false) => {
    const {
      massSelectionEnabled,
      headerSelection
    } = this.state;
    const massAction = massSelectionEnabled && (selectAll || headerSelection);

    let warningFlagsConfiguration: any;
    if (massAction) {
      warningFlagsConfiguration = super.getWarningFlags(action, selectAll);
    } else {
      warningFlagsConfiguration = _.isEmpty(this.warningFlags) ? this.getWarningsFlagsForSelected(action, selectAll) : this.warningFlags;
      delete warningFlagsConfiguration.flagSum;
      if (!warningFlagsConfiguration.actionCannotBeRevertedEnabled && warningFlagsConfiguration.promoteDemoteDisclaimerEnabled) {
        warningFlagsConfiguration.promoteDemoteDisclaimerEnabled = false;
      }
      const flagSum = Object.keys(warningFlagsConfiguration).every((key: string) => !warningFlagsConfiguration[key]);
      warningFlagsConfiguration = {
        ...warningFlagsConfiguration,
        flagSum
      };
      this.warningFlags = warningFlagsConfiguration;
    }
    return warningFlagsConfiguration;
  };


  /**
   * Phase 1 of validation
   * Check if some of conditions is true if that is the case prevents follow-up promote/demote actions
   * used for single selection
   *
   * @param action
   * @param massAction
   */
  checkForSelectionIrregularity = (action: string, massAction: boolean) => {
    const { data } = this.state;
    let isMultiPhase = false;
    let isMultiWorkflow = false;
    let hasCompletedFiles = false;
    let linguistWithoutUpload = false;
    let notClaimed = false;
    let counter = 0;
    let phases: string[] = [];
    let wokflows: string[] = [];

    const selectedData: TARGET[] = data.filter((file: TARGET) => file.selected);
    while (counter < selectedData?.length && !massAction && !(isMultiPhase || isMultiWorkflow || hasCompletedFiles)) {
      const {
        phaseName,
        phaseType,
        status,
        targetId,
        workflowConfiguration
      } = selectedData[counter];

      if (targetId) {
        const { workflowDefinitionId } = workflowConfiguration;

        if (!notClaimed) {
          notClaimed = status.name === 'READY';
        }
        if (phaseName) {
          phases = phases.includes(phaseName) ? phases : phases.concat(phaseName);
          isMultiPhase = phases.length > 1;
        }
        if (workflowDefinitionId) {
          wokflows = wokflows.includes(workflowDefinitionId) ? wokflows : wokflows.concat(workflowDefinitionId);
          isMultiWorkflow = wokflows.length > 1;
        }
        if (!hasCompletedFiles) {
          hasCompletedFiles = _.includes(['DELIVERED', 'PROCESSED'], status.name);
        }
        if (action === 'promote' && !linguistWithoutUpload) {
          linguistWithoutUpload = phaseType === 'linguist' && status.name !== 'UPLOADED';
        }
      }
      counter++;
    }

    return {
      hasCompletedFiles,
      notClaimed,
      isMultiPhase,
      isMultiWorkflow,
      linguistWithoutUpload
    }
  };

  /**
   * Comment files action handler
   * Show warning or open file comments dialog
   * @param event
   */
  commentFiles = (event: DropDownButtonItemClickEvent) => {
    const { item } = event;
    const { submissionData, urlConfigs } = this.props;


    let folder = urlConfigs.paramsConfig.folder;
    let submissionId = submissionData.submissionId;

    let {
      showCommentsNotConfigured,
      showNotClaimed,
      showNotSameBatchOrPhase
    } = this.commentFilesCheckForWarnings(item);
    if (showCommentsNotConfigured || showNotClaimed || showNotSameBatchOrPhase) {
      let warningDialog;
      if (showNotSameBatchOrPhase) {
        warningDialog = 'showNotSameBatchOrPhase';
      } else if (showCommentsNotConfigured) {
        warningDialog = 'showCommentsNotConfigured';
      } else if (showNotClaimed) {
        warningDialog = 'showNotClaimed';
      }
      this.dispatch('showCommentsWarning', '', { warningDialog, all: item.all, actionText: 'comment' });
    } else {
      const { data, cancelledTargetsCount, total, massSelectionEnabled, headerSelection } = this.state;
      let filesCount;

      let massSelectionActive = massSelectionEnabled && (headerSelection || item.all);
      if (massSelectionActive || item.all) {
        filesCount = total - cancelledTargetsCount;
      } else {
        filesCount = _.filter(data, (item: TARGET) => item.selected && item.status.name !== 'CANCELLED').length;
      }
      this.dispatch('openFileCommentsDialog', '', { folder, submissionId, selectAll: item.all, filesCount });
    }
  }

  /**
   * Check if some warning dialogs need to be opened
   * @param action
   */
  commentFilesCheckForWarnings = (action: { all: boolean, taskId: number }) => {
    const {
      data,
      massSelectionEnabled,
      massSelectionConfiguration,
      headerSelection
    } = this.state;

    const { isSupplierView } = this.props;
    let showNotClaimed;
    let showNotSameBatchOrPhase;
    let showCommentsNotConfigured;
    const massSelectionActive = massSelectionEnabled && (headerSelection || action.all);

    if (massSelectionActive) {
      showNotClaimed = !isSupplierView && !massSelectionConfiguration.allClaimed;
      showNotSameBatchOrPhase = !isSupplierView && (massSelectionConfiguration.multiplePhasesSelected || massSelectionConfiguration.multipleWorkflowsSelected);
      showCommentsNotConfigured = false;
    } else {
      let selectedFiles: TARGET[] = action.all ? data : _.filter(data, { selected: true });
      let sameBatch: string | boolean = isSupplierView ? true : selectedFiles[0].batchName;
      let samePhase: string | boolean | null = isSupplierView ? true : selectedFiles[0].phaseName;
      let fileCompleted = false;
      let completedStatuses = ['PROCESSED', 'COMPLETED'];
      let claimed = true;
      let commentsConfigured = true;
      let selectedFilesLength = selectedFiles.length;
      let i = 0;
      let current: TARGET;

      while (i < selectedFilesLength && sameBatch && samePhase && commentsConfigured && !fileCompleted) {
        current = selectedFiles[i];
        if (!isSupplierView && sameBatch !== current.batchName) {
          sameBatch = false;
        }
        if (!isSupplierView && samePhase !== current.phaseName) {
          samePhase = false;
        }
        if (completedStatuses.includes(current.status.name)) {
          fileCompleted = true;
        }
        let notClaimedStatus = isSupplierView ? 'NOT_CLAIMED' : 'READY';
        if (claimed) {
          claimed = current.status.name !== notClaimedStatus;
        }
        if (!action.all && current.availableTasks && !fileCompleted && claimed) {
          const binaryTasksAndProcessing = getBinaryReversed(current.availableTasks);
          const availableTaskIds = getAvailableTaskIds(binaryTasksAndProcessing);
          commentsConfigured = availableTaskIds.includes(action.taskId);
        }

        i++;
      }
      showNotClaimed = !claimed;
      showNotSameBatchOrPhase = !sameBatch || !samePhase || fileCompleted;
      showCommentsNotConfigured = !commentsConfigured;
    }

    return { showNotClaimed, showNotSameBatchOrPhase, showCommentsNotConfigured };
  }

  /**
   * Toolbar render
   */
  toolbarRender = (): ReactElement<PuzzleToolbar> => {
    const { filesGridTasks } = this.state;

    return (
      <PuzzleToolbar
        className={'tp-topbar-actions'}
      >
        {
          filesGridTasks.map((item: GROUPED_TASK) => {
            if (item.groupName === "upload nui") {
              return (
                <React.Fragment
                  key={item.groupName + "_group"}
                >
                  <div className={'tp-separator'} />
                  <PuzzleButton
                    themeColor={'primary'}
                    onClick={() => this.uploadFiles()}
                  >
                    {this.translate('upload')}
                  </PuzzleButton>
                </React.Fragment>
              )
            } else if (_.includes(['open', 'preview'], item.groupName)) {
              return this.openButtonRender(item.groupName);
            }
            return (
              <PuzzleDropDownButton
                icon={'arrow-s'}
                themeColor={'dark'}
                reverse
                key={item.groupName + "_group"}
                popupSettings={popupAlignment('tp-custom-dropdown-list tp-download-list')}
                items={this.dropDownItemsRender(item.elements)}
                onItemClick={this.itemClick}
                text={this.translate('action_group_names.' + item.groupName)}
                itemRender={(propsItem: any) => (
                  <div>
                    {propsItem.item.text}
                  </div>
                )}
              />
            )
          })
        }
      </PuzzleToolbar>
    )
  };

  /**
   * Handler for dropdown items click.
   * @param element
   */
  itemClick = (element: DropDownButtonItemClickEvent): void => {
    let {
      groupName,
    } = element.item;

    switch (groupName) {
      case "assign":
        this.assignFiles(element.item);
        break;
      case "download":
        this.downloadFiles(element);
        break;
      case "comment":
        this.commentFiles(element);
        break;
      case "complete":
        this.completeReturnFilesHandler('promote', element);
        break;
      case "grading and scoring":
        this.gradeAndScore(element.item);
        break;
      case "return":
        this.completeReturnFilesHandler('demote', element);
        break;
      case "open":
      case "preview":
        this.openInEditor(element.item, groupName);
        break;
      default:
        console.error('Invalid group name');
        break;
    }
  };

  /**
   * Drop down items render
   * @param props
   */
  dropDownItemsRender = (props: ACTION[]) => {
    let {
      cancelledTargetsCount,
      massSelectionEnabled,
      total,
      headerSelection
    } = this.state;
    let totalWithoutCancelled = total - cancelledTargetsCount;
    let selectedTargets = (massSelectionEnabled && headerSelection) ? totalWithoutCancelled : this.getSelectedCount();

    return props.map((item) => {
      const {
        groupName,
        selectStyle,
        taskName,
        taskId
      } = item;

      let allItems = selectStyle === 0; //action for all items
      let disabled = allItems ? false : !selectedTargets;
      let numberOfItems = allItems || (massSelectionEnabled && headerSelection) ? totalWithoutCancelled : this.getSelectedCount();
      return {
        disabled,
        all: selectStyle === 0,
        groupName,
        taskName,
        taskId,
        text: this.translate(`actions.${groupName}.${taskName}`, { length: numberOfItems }),
        ...(item.groupName === 'download' && {
          downloadType: this.getDownloadType(taskName)
        })
      }
    });

  };

  /**
   * Returns appropriate downloadType relative to taskName
   * @param taskName
   */
  getDownloadType = (taskName: string): string => {
    let downloadType = '';
    if (taskName.includes('kit')) {
      downloadType = 'localizationKit';
    } else if (taskName.includes('deliverables')) {
      downloadType = 'deliverables';
    } else if (taskName.includes('translatable')) {
      downloadType = 'TXLFFiles';
    } else if (taskName.includes('glp')) {
      downloadType = 'WF5';
    }

    return downloadType;
  };

  /**
   * Returns text w/ claim only or claim and open in addition based on passed prop
   *
   * @param onlineEditorParams Object with online editors info
   * @param actionText Action text
   */
  getClaimAndOpenText = (onlineEditorParams: any, actionText: string) => {
    let textString;
    if (onlineEditorParams) {
      textString = this.translate('claimAndOpenText', { actionText });
    } else if (actionText === 'comment') {
      textString = this.translate('claimForComment');
    } else {
      textString = this.translate('claimText', { actionText });
    }
    const titleString = actionText === 'comment' ? this.translate('claimAndOpenTitleComments') : this.translate('claimAndOpenTitle');
    const result = {
      text: checkAndConvertToStaticMarkup(textString),
      title: titleString
    }

    return result;
  };

  /**
   * Updates data for files grid search with data from filter
   */
  updateDataWithFilterParams = (params: any, filesGridFilter: any) => {
    let clonedFilter = { ...filesGridFilter };
    Object.assign(params, clonedFilter);
    return params;
  };

  /**
   * Upload request params getter
   */
  getUploadParams = (): UPLOAD_REQUEST_PARAMS => {
    const { getUploadParams } = this.props.overrideMethods;
    let uploadParams;
    if (getUploadParams) {
      uploadParams = getUploadParams();
    } else {
      const { submissionId } = this.props.submissionData;
      uploadParams = {
        completeActionName: 'reportDialogCompleteAll',
        returnActionName: 'reportDialogReturnAll',
        downloadReportUrl: '/submissions/download/upload/report',
        uploadDialogUrl: `/submissions/${submissionId}/upload`,
        uploadReportGetUrl: `/submissions/${submissionId}/upload/report/`
      }
    }

    return uploadParams;
  }

  /**
   * Toggle scroll on files grid
   * @param disableFilesScroll
   */
  toggleScroll = (disableFilesScroll: boolean) => {
    this.setState({
      disableFilesScroll
    });
  }

  /**
   * Open upload dialog
   * @param files
   */
  uploadFiles = (files?: FileList) => {
    const {
      projectId,
      submissionId,
      submissionName,
      taskId
    } = this.props.submissionData;
    const submissionData = {
      projectId,
      submissionId,
      submissionName,
      taskId
    };

    const uploadParams = this.getUploadParams();
    this.dispatch('openUploadDialog', '', {
      ...uploadParams,
      submissionData,
      ...(files && { droppedFiles: files })
    });
  };

  /**
   * Vendor column cell render
   */
  vendorRenderer = (cellProps: GridCellProps) => {
    let field = cellProps.field;
    let value;
    if (field) {
      value = cellProps.dataItem[field];
    }
    return (
      <>
        {/*Add button link ui for this when action acctualy works */}
        <span onClick={this.onVendorNameClick.bind(this, cellProps.dataItem)}>
          {value}
        </span>
      </>
    )
  };

  /**
   * Words column cell renderer
   */
  wordsCellRender = (cellProps: GridCellProps) => {
    const { wordsCellRender } = this.props.overrideMethods;
    let loadingCell = this.loadingCell(cellProps);
    let render;
    if (wordsCellRender && _.isNull(loadingCell)) {
      render = wordsCellRender(cellProps);
    } else {
      const { dataItem } = cellProps;
      const { batchName, targetLanguage } = dataItem
      const preExpandJob = {
        batchName: batchName,
        targetLanguage: targetLanguage?.locale
      }
      render = loadingCell ? loadingCell : (
        <WordCountLink
          groupBy="languages"
          data={{ ...cellProps.dataItem, partiallyAnalyzed: this.props.submissionData?.partiallyAnalyzed }}
          onlyNumber={true}
          preExpandJob={preExpandJob}
        />
      )
    }

    return render;
  };

  renderBody() {
    const {
      data,
      dataLoadDone,
      disableFilesScroll,
      filesGridTasks,
      skip,
      sort,
      total
    } = this.state;
    const availableColumns = this.mapGridConfigItems();
    const { fileNameInputText } = this.state.filters;
    const { rowHeight, selectedFieldName, isSupplierView } = this.props;
    const gridCls = classNames(
      'tp-main-grid tp-drawer-files-tab-grid', {
        'tp-empty-grid': !data.length,
        'tp-disable-scroll': disableFilesScroll
      }
    );
    let placeholderText = isSupplierView ? 'search' : 'searchByFileName';
    let uploadNuiExists = _.find(filesGridTasks, { groupName: 'upload nui' });
    return (
      <Container className={'tp-drawer-body-tab-content'}>
        {this.toolbarRender()}
        <Container
          ref={(el: any) => this.settingsButtonRef = el}
          className={'tp-drawer-body-tab-files-topbar'}
          alignItemsCenter
          hBox
          justifyContentBetween
          wrap
        >
          <span className={'tp-search-files-wrap k-d-flex k-flex-1'}>
            <PuzzleInput
              searchField={isSupplierView}
              onChange={this.handleInputChange}
              placeholder={this.translate(placeholderText)}
              value={fileNameInputText}
            />
            {this.searchResultsRender()}
          </span>
          <Container
            className={'tp-files-grid-action-btn'}
            displayFlex
            justifyContentEnd
          >
            <PuzzleButton
              themeColor={'primary'}
              fillMode={'outline'}
              iconClass={'tp-icon tp-clear-filters'}
              onClick={this.resetFilters}
            />
            <PuzzleButton
              ref={(el: any) => this.columnButtonConfig = el}
              aria-label={this.translate('SRL-filesGridColumn')}
              iconClass="tp-icon tp-columnsNew"
              fillMode="outline"
              themeColor={'primary'}
              className="tp-with-bg"
              onClick={() => {
                const { showGridColumnSettings } = this.state;
                this.setState({ showGridColumnSettings: !showGridColumnSettings })
              }}
            />
            <PuzzleButton
              aria-label={this.translate('SRL-refreshFilesGrid')}
              themeColor={'primary'}
              fillMode="outline"
              iconClass="tp-icon tp-refresh"
              onClick={this.refreshFilesGrid}
            />
          </Container>
        </Container>
        <Popup
          anchorAlign={{ horizontal: 'right', vertical: 'bottom' }}
          popupAlign={{ horizontal: 'right', vertical: 'top' }}
          anchor={this.settingsButtonRef}
          onOpen={this.columnsPopupOnOpen}
          onClose={() => {
            this.columnPopupArrowIndex = 0;
            this.columnButtonConfig.getDomNode().focus();
          }}
          show={this.state.showGridColumnSettings}
        >
          <ul
            className="k-widget k-reset k-header k-menu k-menu-vertical tp-main-grid-column-list"
            ref={(el: any) => this.gridSettingsDropdownRef = el?.element || el}
          >
            {availableColumns.map(this.renderGridConfigItem)}
          </ul>
        </Popup>
        <div
          className={'tp-drawer-body-tab-files-content tp-main-grid-wrap'}
          ref={this.props.innerRef}
        >
          <KendoGridAbstract
            captionText={this.translate('SRL-filesTableCaptionText')}
            ref={(el) => this.filesGridRef = el?.gridRef}
            className={gridCls}
            dNdMessage={this.translate('dragAndDropText')}
            dragAndDropEnabled={uploadNuiExists}
            data={data.slice(skip, skip + this.props.domPageSize)}
            onSortChange={this.onSortChange}
            onHeaderSelectionChange={this.headerSelectionChange}
            onPageChange={this.pageChange}
            onSelectionChange={this.selectionChange}
            onColumnResize={this.onColumnResize}
            pageSize={this.props.domPageSize}
            rowHeight={rowHeight}
            rowRender={this.rowRender}
            scrollable={'virtual'}
            selectedField={selectedFieldName}
            sort={sort}
            sortable={{ allowUnsort: false }}
            skip={skip}
            resizable
            total={total}
            uploadFiles={this.uploadFiles}
          >
            <GridNoRecords>
              {dataLoadDone && !data.length ?
                (<div className='k-vbox k-justify-content-center k-align-items-center'>
                  <div className={`tp-grid-empty-state-img no-documents`} />
                  <div
                    className='tp-grid-empty-state-text tp-color-light-LM tp-text-semibold k-text-center tp-font-size-xl'>
                    {this.translate('gridEmptyLabel')}
                  </div>
                </div>) : (<div></div>)
              }
            </GridNoRecords>
            {this.state.gridConfig.filter((this.isHidden)).map(this.renderColumn)}
          </KendoGridAbstract>
        </div>
      </Container>
    );
  }

  /**
   * Map for quick filters. Key is column's 'dataIndex' and value is matching 'filter.name'
   * note: Since some dataIndexes includes dot, those were split by '.' and first word is taken here
   */
  GET_FILTERNAME_MAP = {
    fileName: 'fileNameInputText',
    batchName: 'batchNameInputText',
    targetLanguage: 'targetLanguageValues',
    phaseName: 'phaseName',
    grade: 'gsStatusCombo',
    status: 'phaseStatusValues',
    vendorName: 'vendorValues',
    phaseOwner: 'userNameInputText'
  };
}

export default withDynamicSize(Files);
